import React, { Component } from 'react';
import {
  Undo,
  Redo,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatListBulleted,
  FormatListNumbered,
  FormatClear,
} from '@material-ui/icons';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { AnimatePresence } from "framer-motion";
import { SnippetButton } from './SnippetButton';
import Link from './Link';
import Table from './Table';
import Video from './Video';
import FontSize from './FontSize';
import TableControls from './TableControls';
import Image from './Image';
import LineHeight from './LineHeight';
import FontColor from './FontColor';
import Hotkeys from './Hotkeys';
import Snippets from './Snippets';
import Translations from './Translations';

import { MARKS, NODES } from '../../../constants';
import Headings from './Headings';

class Toolbar extends Component {
  static propTypes = {
    editor: PropTypes.object,
    value: PropTypes.object,
    redo: PropTypes.func,
    undo: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {};

    this.toggleMark = this.toggleMark.bind(this);
    this.toggleNode = this.toggleNode.bind(this);
    this.harMark = this.harMark.bind(this);
    this.hasNode = this.hasNode.bind(this);
    this.hasListType = this.hasListType.bind(this);
    this.clearFormatting = this.clearFormatting.bind(this);
  }

  toggleMark(event) {
    event.preventDefault();
    const type = event.currentTarget.dataset.type;
    this.props.editor.toggleMark(type);
  }

  toggleNode(event) {
    event.preventDefault();
    const type = event.currentTarget.dataset.type;
    const isList = this.hasNode(NODES.LIST_ITEM);
    const defaultNode = NODES.PARAGRAPH;
    if (type !== NODES.UNORDERED_LIST && type !== NODES.ORDERED_LIST) {
      const isActive = this.hasNode(type);
      if (isList) {
        this.props.editor
          .setBlocks(isActive ? defaultNode : type)
          .unwrapBlock(NODES.ORDERED_LIST)
          .unwrapBlock(NODES.UNORDERED_LIST);
      } else {
        this.props.editor.setBlocks(isActive ? defaultNode : type);
      }
    } else {
      const isType = this.hasListType(type);
      if (isList && isType) {
        // this means we're to toggle off existing list block
        this.props.editor
          .setBlocks(defaultNode)
          .unwrapBlock(NODES.ORDERED_LIST)
          .unwrapBlock(NODES.UNORDERED_LIST);
      } else if (isList) {
        // this means we're switching list type
        this.props.editor
          .unwrapBlock(
            type === NODES.ORDERED_LIST
              ? NODES.UNORDERED_LIST
              : NODES.ORDERED_LIST
          )
          .wrapBlock(type);
      } else {
        this.props.editor.setBlocks(NODES.LIST_ITEM).wrapBlock(type);
      }
    }
  }

  harMark(type) {
    return this.props.value.activeMarks.some(mark => mark.type === type);
  }

  hasNode(type) {
    return this.props.value.blocks.some(node => node.type === type);
  }

  hasListType(type) {
    return this.props.value.blocks.some(block => {
      return Boolean(
        this.props.value.document.getClosest(
          block.key,
          parent => parent.type === type
        )
      );
    });
  }

  clearFormatting(event) {
    event.preventDefault();

    this.props.editor.setBlocks(NODES.PARAGRAPH);
    this.props.editor.removeMark(MARKS.BOLD);
    this.props.editor.removeMark(MARKS.ITALIC);
    this.props.editor.removeMark(MARKS.UNDERLINE);
    this.props.editor.unwrapBlock(NODES.PARAGRAPH_LEFT);
    this.props.editor.unwrapBlock(NODES.PARAGRAPH_RIGHT);
    this.props.editor.unwrapBlock(NODES.PARAGRAPH_CENTER);
    this.props.editor.unwrapBlock(NODES.PARAGRAPH_JUSTIFY);
    this.props.editor.unwrapBlock(NODES.PARAGRAPH_JUSTIFY);
    this.props.editor.unwrapInline(NODES.LINK);
    this.props.editor.unwrapBlock(NODES.LIST_ITEM);
    this.props.editor.unwrapBlock(NODES.ORDERED_LIST);
    this.props.editor.unwrapBlock(NODES.UNORDERED_LIST);

    this.props.value.marks.forEach(mark => {
      const currentMarkSize = mark.data.get('size');
      this.props.editor.removeMark({
        type: MARKS.FONT_SIZE,
        data: { size: currentMarkSize },
      });
    });

    this.props.value.marks.forEach(mark => {
      const currentMarkHeight = mark.data.get('lineHeight');
      this.props.editor.removeMark({
        type: MARKS.LINE_HEIGHT,
        data: { lineHeight: currentMarkHeight },
      });
    });

    this.props.value.marks.forEach(mark => {
      const currentMarkcolor = mark.data.get('color');
      this.props.editor.removeMark({
        type: MARKS.FONT_COLOR,
        data: { color: currentMarkcolor },
      });
    });
  }

  render() {
    const undos = this.props.value.data.get('undos');
    const redos = this.props.value.data.get('redos');
    const showTableControls = this.props.editor &&
      this.props.editor.isSelectionInTable(this.props.value);

    return (
      <div className="editor__toolbar">
        <button
          onClick={this.props.undo}
          title="Undo"
          className={cx('icon__button', { disable: !(undos && undos.size) })}>
          <Undo />
        </button>
        <button
          onClick={this.props.redo}
          title="Redo"
          className={cx('icon__button', { disable: !(redos && redos.size) })}>
          <Redo />
        </button>
        <Headings {...this.props} />
        <button
          onClick={this.toggleMark}
          data-type={MARKS.BOLD}
          title="Bold"
          className={cx('icon__button', { active: this.harMark(MARKS.BOLD) })}>
          <FormatBold />
        </button>
        <button
          data-type={MARKS.ITALIC}
          title="Italic"
          className={cx('icon__button', { active: this.harMark(MARKS.ITALIC) })}
          onClick={this.toggleMark}>
          <FormatItalic />
        </button>
        <button
          data-type={MARKS.UNDERLINE}
          title="Underline"
          className={cx('icon__button', {
            active: this.harMark(MARKS.UNDERLINE),
          })}
          onClick={this.toggleMark}>
          <FormatUnderlined />
        </button>
        <button
          data-type={NODES.PARAGRAPH_LEFT}
          title="Align Left"
          className={cx('icon__button', {
            active: this.hasNode(NODES.PARAGRAPH_LEFT),
          })}
          onClick={this.toggleNode}>
          <FormatAlignLeft />
        </button>
        <button
          data-type={NODES.PARAGRAPH_CENTER}
          title="Align Center"
          className={cx('icon__button', {
            active: this.hasNode(NODES.PARAGRAPH_CENTER),
          })}
          onClick={this.toggleNode}>
          <FormatAlignCenter />
        </button>
        <button
          data-type={NODES.PARAGRAPH_RIGHT}
          title="Align Right"
          className={cx('icon__button', {
            active: this.hasNode(NODES.PARAGRAPH_RIGHT),
          })}
          onClick={this.toggleNode}>
          <FormatAlignRight />
        </button>
        <button
          data-type={NODES.PARAGRAPH_JUSTIFY}
          title="Align Justify"
          className={cx('icon__button', {
            active: this.hasNode(NODES.PARAGRAPH_JUSTIFY),
          })}
          onClick={this.toggleNode}>
          <FormatAlignJustify />
        </button>
        <button
          data-type={NODES.UNORDERED_LIST}
          title="Bullet List"
          className={cx('icon__button', {
            active:
              this.hasNode(NODES.LIST_ITEM) &&
              this.hasListType(NODES.UNORDERED_LIST),
          })}
          onClick={this.toggleNode}>
          <FormatListBulleted />
        </button>
        <button
          data-type={NODES.ORDERED_LIST}
          title="Number List"
          className={cx('icon__button', {
            active:
              this.hasNode(NODES.LIST_ITEM) &&
              this.hasListType(NODES.ORDERED_LIST),
          })}
          onClick={this.toggleNode}>
          <FormatListNumbered />
        </button>
        <Image {...this.props} />
        <Video {...this.props} />
        <Link {...this.props} />
        <SnippetButton {...this.props} />
        <Table {...this.props} />
        <AnimatePresence>
          {showTableControls && <TableControls {...this.props} />}
        </AnimatePresence>
        <Snippets {...this.props} />
        <Translations {...this.props} />
        <FontSize {...this.props} />
        <FontColor {...this.props} />
        <LineHeight {...this.props} />
        <button
          title="Clear Formatting"
          onClick={this.clearFormatting}
          className="icon__button">
          <FormatClear />
        </button>
        <Hotkeys {...this.props} />
      </div>
    );
  }
}

export default  Toolbar;
